import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import NutritionLabel from "@/components/NutritionLabel";
import { computeLabelReadiness, readinessStatus } from "@/lib/readiness";
import { calculateAverageDensity, usesLiquidMeasurements } from "@/lib/product-types";
import {
  PencilSimple, ArrowLeft, Tag, Package, ClipboardText,
  GitBranch, ForkKnife, FileText, Warning,
} from "@phosphor-icons/react";

// ── Commercialization Center ──────────────────────────────────────────────
// One hub page per product: readiness status, formula, specifications, label,
// nutrition, packaging, documentation, and version history — the product's
// commercialization operating view (human FDA path; pet products use the
// builder's AAFCO path instead).

const STATUS_STYLES = {
  Production: "bg-emerald-100 text-emerald-800",
  Pilot: "bg-amber-100 text-amber-800",
  Archived: "bg-stone-100 text-stone-500",
  "R&D": "bg-blue-100 text-blue-800",
};

export default function CommercializationCenter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const bp = user?.business_profile || {};
  const [recipe, setRecipe] = useState(null);
  const [ingredientsById, setIngredientsById] = useState({});
  const [calc, setCalc] = useState(null);
  const [family, setFamily] = useState([]); // version history (same lineage)
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: r }, { data: ings }, { data: all }] = await Promise.all([
          api.get(`/recipes/${id}`),
          api.get("/ingredients"),
          api.get("/recipes"),
        ]);
        if (cancelled) return;
        if (r.product_type === "pet_topper") {
          // Pet products follow the AAFCO path — no FDA readiness hub.
          navigate(`/builder/${id}`, { replace: true });
          return;
        }
        setRecipe(r);
        setIngredientsById(Object.fromEntries(ings.map((x) => [x.id, x])));
        // Version lineage: root is the recipe's parent (or itself); members are
        // the root plus every recipe pointing at that root.
        const rootId = r.parent_recipe_id || r.id;
        setFamily(
          all
            .filter((m) => m.id === rootId || m.parent_recipe_id === rootId)
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        );
        // Live nutrition + statement + claims for the readiness headline
        const { data: c } = await api.post("/calculate", {
          name: r.name || "Untitled",
          product_name: r.product_name || "",
          serving_size_g: Number(r.serving_size_g) || 28,
          servings_per_container: Number(r.servings_per_container) || 1,
          unit_size_g: r.unit_size_g ?? null,
          ingredients: (r.ingredients || []).map((ri) => ({
            ingredient_id: ri.ingredient_id, grams: Number(ri.grams),
          })),
          declared_soi: r.declared_soi || null,
          product_type: r.product_type || "dry_blend",
          species: null,
          ash_pct: r.ash_pct ?? null,
          moisture_max_pct: r.moisture_max_pct ?? null,
        });
        if (!cancelled) setCalc(c);
      } catch (err) {
        console.error("Commercialization Center load failed", err);
        if (!cancelled) setError(err);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const isLiquid = usesLiquidMeasurements(recipe?.product_type, recipe?.product_form);

  const avgDensity = useMemo(() => {
    if (!recipe || !isLiquid) return 1.0;
    return calculateAverageDensity(
      recipe.ingredients,
      (row) => ingredientsById[row.ingredient_id]?.density_g_ml
    );
  }, [recipe, ingredientsById, isLiquid]);

  // Net quantity for the readiness check — derived from pack-out since net
  // weight itself isn't persisted (the builder derives it the same way).
  const netQuantity = useMemo(() => {
    if (!recipe) return "";
    if (isLiquid && recipe.unit_size_g) {
      if (recipe.unit_label && /\b(fl\s*oz|mL|L)\b/i.test(recipe.unit_label)) {
        return recipe.unit_label;
      }
      const ml = Number(recipe.unit_size_g) / (avgDensity || 1);
      const flOz = ml / 29.5735;
      return `${flOz.toFixed(flOz < 10 ? 1 : 0)} fl oz (${Math.round(ml)} mL)`;
    }
    if (recipe.unit_label && recipe.unit_size_g) return `${recipe.unit_label} (${recipe.unit_size_g}g)`;
    if (recipe.unit_size_g) return `${recipe.unit_size_g}g`;
    return recipe.unit_label || "";
  }, [recipe, isLiquid, avgDensity]);

  const readiness = useMemo(() => {
    if (!calc || !recipe) return null;
    return computeLabelReadiness({
      calc, bp, netWeight: netQuantity, productName: recipe.product_name || recipe.name,
    });
  }, [calc, recipe, bp, netQuantity]);

  const totalG = useMemo(
    () => (recipe?.ingredients || []).reduce((a, r) => a + Number(r.grams || 0), 0),
    [recipe]
  );

  if (error) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Warning size={32} className="text-amber-500 mx-auto" />
          <p className="mt-3 text-stone-600">Could not load this product.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/dashboard")}>
            <ArrowLeft size={14} className="mr-2" /> Back to dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  if (!recipe) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-16 text-stone-500">Loading…</div>
      </Layout>
    );
  }

  const status = readiness ? readinessStatus(readiness.score) : null;
  const mark = (s) => (s === "pass" ? "✓" : s === "warn" ? "⚠" : "✗");
  const markColor = (s) =>
    s === "pass" ? "text-emerald-700" : s === "warn" ? "text-amber-600" : "text-red-700";

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="commercialization-center">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <Link to="/dashboard" className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1">
              <ArrowLeft size={12} /> Dashboard
            </Link>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-2">Commercialization Center</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="cc-product-name">
              {recipe.product_name || recipe.name}
            </h1>
            <div className="flex items-center gap-2 text-xs mt-2 flex-wrap">
              {recipe.sku && <span className="font-mono text-stone-700 bg-stone-100 px-1.5 py-0.5 rounded">{recipe.sku}</span>}
              <span className="text-stone-500">{recipe.version || "v1"}</span>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${STATUS_STYLES[recipe.status] || STATUS_STYLES["R&D"]}`}>
                {recipe.status || "R&D"}
              </span>
            </div>
          </div>
          <Button onClick={() => navigate(`/builder/${recipe.id}`)}
            className="bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="cc-open-builder">
            <PencilSimple size={15} className="mr-2" /> Open in Builder
          </Button>
        </div>

        {/* Headline: Commercial Readiness */}
        <Card className="p-5 border-stone-200 mb-6" data-testid="cc-readiness">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">Commercial Readiness Check</div>
            {status ? (
              <div className={`text-lg font-bold ${status.cls}`} data-testid="cc-readiness-score">
                {status.dot} {status.text} <span className="font-normal text-stone-400 text-sm">({readiness.score}%)</span>
              </div>
            ) : (
              <div className="text-sm text-stone-400">Calculating…</div>
            )}
          </div>
          {readiness && (
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {readiness.checks.map((c, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs leading-tight">
                  <span className={`font-bold ${markColor(c.status)}`}>{mark(c.status)}</span>
                  <span>
                    <span className={c.status === "fail" ? "text-red-700" : "text-stone-700"}>{c.label}</span>
                    {c.note && <span className="text-stone-400"> — {c.note}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 text-[10px] text-stone-400 leading-tight">
            Checks that common required label elements are present. Not a legal compliance review.
          </div>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {/* Left 2/3: formula, specs, packaging, documentation, versions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Formula summary */}
            <Section icon={ForkKnife} title="Formula" testId="cc-formula">
              {(recipe.ingredients || []).length === 0 ? (
                <p className="text-sm text-stone-500">No ingredients yet — add them in the builder.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-stone-400 text-left">
                      <th className="pb-1 font-semibold">Ingredient</th>
                      <th className="pb-1 font-semibold text-right">Grams</th>
                      <th className="pb-1 font-semibold text-right">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(recipe.ingredients || [])]
                      .sort((a, b) => Number(b.grams) - Number(a.grams))
                      .map((ri, i) => (
                        <tr key={i} className="border-t border-stone-100">
                          <td className="py-1.5 text-stone-800">
                            {ingredientsById[ri.ingredient_id]?.name || ri.ingredient_id}
                          </td>
                          <td className="py-1.5 text-right font-mono text-stone-600">{Number(ri.grams).toFixed(2)}</td>
                          <td className="py-1.5 text-right font-mono text-stone-600">
                            {totalG ? ((Number(ri.grams) / totalG) * 100).toFixed(1) : "0.0"}%
                          </td>
                        </tr>
                      ))}
                    <tr className="border-t-2 border-stone-200 font-semibold">
                      <td className="py-1.5">Total</td>
                      <td className="py-1.5 text-right font-mono">{totalG.toFixed(2)}</td>
                      <td className="py-1.5 text-right font-mono">100%</td>
                    </tr>
                  </tbody>
                </table>
              )}
              {calc?.statement?.statement && (
                <div className="mt-3 pt-3 border-t border-stone-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-stone-400 mb-1">Ingredient statement</div>
                  <p className="text-xs text-stone-700"><b>INGREDIENTS:</b> {calc.statement.statement}</p>
                  {calc.statement.contains_line && (
                    <p className="text-xs text-stone-700 mt-1 font-semibold">{calc.statement.contains_line}</p>
                  )}
                </div>
              )}
            </Section>

            {/* Specifications */}
            <Section icon={ClipboardText} title="Specifications" testId="cc-specs">
              <FieldGrid
                fields={[
                  ["Description", recipe.description],
                  ["Aroma profile", recipe.aroma_profile],
                  ["Flavor profile", recipe.flavor_profile],
                  ["Color", recipe.color],
                  ["Mesh size", recipe.mesh_size],
                  ["Bulk density (g/mL)", recipe.bulk_density_g_ml],
                  ["Moisture max (%)", recipe.moisture_max_pct],
                  ["Shelf life (months)", recipe.shelf_life_months],
                  ["Storage conditions", recipe.storage_conditions],
                  ["Micro spec", recipe.micro_spec],
                  ["Application notes", recipe.application_notes],
                ]}
                emptyNote="No specifications yet — fill in the spec sheet in the builder."
              />
            </Section>

            {/* Packaging & pack-out */}
            <Section icon={Package} title="Packaging & pack-out" testId="cc-packaging">
              <FieldGrid
                fields={[
                  ["Retail unit", recipe.unit_label],
                  ["Net weight per unit (g)", recipe.unit_size_g],
                  ["Bulk packaging", recipe.packaging],
                  ["Serving size (g)", recipe.serving_size_g],
                  ["Servings per container", recipe.servings_per_container],
                ]}
                emptyNote="No packaging info yet — set pack-out details in the builder."
              />
            </Section>

            {/* Documentation */}
            <Section icon={FileText} title="Documentation" testId="cc-docs">
              <FieldGrid
                long
                fields={[
                  ["Production method (commercial)", recipe.production_method],
                  ["Lab-scale production method", recipe.lab_production_method],
                  ["Bench QA procedure", recipe.lab_bench_procedure],
                  ["R&D notes", recipe.notes],
                  ["Safety plan", recipe.safety_plan ? "Draft hazard-analysis worksheet on file (see builder)" : ""],
                ]}
                emptyNote="No documentation yet — production methods, lab procedures and notes live in the builder."
              />
            </Section>

            {/* Version history */}
            <Section icon={GitBranch} title="Version history" testId="cc-versions">
              {family.length <= 1 ? (
                <p className="text-sm text-stone-500">
                  {recipe.version || "v1"} is the only version. Duplicate the formulation in the builder to branch a new version.
                </p>
              ) : (
                <div className="space-y-1">
                  {family.map((m) => (
                    <Link key={m.id} to={`/product/${m.id}`}
                      className={`flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm hover:bg-stone-50 ${
                        m.id === recipe.id ? "bg-emerald-50/60 ring-1 ring-inset ring-emerald-200" : ""
                      }`}
                      data-testid={`cc-version-${m.id}`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs text-stone-500 shrink-0">{m.version || "v1"}</span>
                        <span className="truncate text-stone-800">{m.product_name || m.name}</span>
                        {m.id === recipe.id && <span className="text-[10px] text-emerald-700 font-bold shrink-0">viewing</span>}
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${STATUS_STYLES[m.status] || STATUS_STYLES["R&D"]}`}>
                          {m.status || "R&D"}
                        </span>
                        <span className="text-xs text-stone-400">{new Date(m.updated_at).toLocaleDateString()}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </Section>
          </div>

          {/* Right 1/3: label & nutrition */}
          <div className="space-y-6">
            <Section icon={Tag} title="Label & nutrition" testId="cc-label">
              {calc?.nutrition ? (
                <>
                  <NutritionLabel calc={calc} isLiquid={isLiquid} density={avgDensity} />
                  <Button variant="outline" className="mt-4 w-full"
                    onClick={() => navigate(`/builder/${recipe.id}`)}
                    data-testid="cc-label-preview-link">
                    Full label preview &amp; print →
                  </Button>
                </>
              ) : (
                <p className="text-sm text-stone-500">
                  Nutrition Facts will appear once the formula has ingredients.
                </p>
              )}
            </Section>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Section({ icon: Icon, title, children, testId }) {
  return (
    <Card className="p-5 border-stone-200" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="duotone" className="text-stone-500" />
        <div className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">{title}</div>
      </div>
      {children}
    </Card>
  );
}

// Renders label/value rows, skipping empty fields; shows a hint when all empty.
function FieldGrid({ fields, emptyNote, long = false }) {
  const filled = fields.filter(([, v]) => v !== "" && v != null);
  if (filled.length === 0) return <p className="text-sm text-stone-500">{emptyNote}</p>;
  return (
    <dl className={long ? "space-y-3" : "grid sm:grid-cols-2 gap-x-6 gap-y-2"}>
      {filled.map(([label, value]) => (
        <div key={label}>
          <dt className="text-[10px] uppercase tracking-wider font-bold text-stone-400">{label}</dt>
          <dd className="text-sm text-stone-800 whitespace-pre-wrap">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}
